import type { SeriesAssessmentAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import AssessmentQ from './AssessmentQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import SeriesQ from './SeriesQ'

const SeriesAssessmentQ = {
  ...makeQueryModelV2<SeriesAssessmentAttributes>('series_assessment')([
    'id',
    'assessment_id',
    'series_id',
    'released_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Series: { query: SeriesQ, foreignKey: SeriesQ.id, sourceKey: SeriesAssessmentQ.series_id },
    Assessment: {
      query: AssessmentQ,
      foreignKey: AssessmentQ.id,
      sourceKey: SeriesAssessmentQ.assessment_id,
    },
  }),

  where: (
    {
      id,
      seriesId,
      assessmentId,
      released,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      id?: number | SQLFragment<number>
      seriesId?: number | SQLFragment<number>
      assessmentId?: number | SQLFragment<number>
      released?: boolean
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesAssessmentAttributes> => SQL`
        FROM ${SeriesAssessmentQ}
        ${includeAssociationsToSql(SeriesAssessmentQ, include)}
        WHERE
            ${id != null ? SQL`${SeriesAssessmentQ.id} = ${id} AND` : SQL``}
            ${seriesId != null ? SQL`${SeriesAssessmentQ.series_id} = ${seriesId} AND` : SQL``}
            ${
              assessmentId != null
                ? SQL`${SeriesAssessmentQ.assessment_id} = ${assessmentId} AND`
                : SQL``
            }
            ${
              released != null
                ? SQL`${SeriesAssessmentQ.released_at} IS ${released ? SQL`NOT ` : SQL``} NULL ${
                    released ? SQL`AND` : SQL`OR`
                  } ${SeriesAssessmentQ.released_at} ${released ? SQL`<=` : SQL`>`} NOW() AND `
                : SQL``
            }
            ${
              released != null
                ? SQL`${released ? SQL`NOT ` : SQL``}(${SeriesAssessmentQ.released_at} IS NULL OR ${
                    SeriesAssessmentQ.released_at
                  } > NOW()) AND `
                : SQL``
            }
            ${paranoid ? SQL`${SeriesAssessmentQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  isAcceptingResponses: (): SQLFragment<boolean> =>
    SQL`${SeriesAssessmentQ.released_at} IS NOT NULL`,
}

export default SeriesAssessmentQ
